import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import supabase from '../../supabaseClient';
import { setUser, setSession } from '../../store/authSlice';
import { createCheckoutSession } from '../../services/documentApi';
import { acceptTerms } from '../../services/termsApi';
import PasswordRequirements from './PasswordRequirements';
import { isPasswordValid } from '../../utils/passwordValidation';

export default function AuthModal({ onClose }) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.zynacular.com/reset-password',
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        // If email confirmation is required, show a success message
        if (data.session) {
          dispatch(setSession(data.session));
          dispatch(setUser(data.session.user));
          // Best-effort — never blocks or fails signup; see termsApi.js.
          // Only reachable here because the checkbox already gated this
          // submit, so acceptance is a given by this point. Awaited (rather
          // than fire-and-forget) so it can't get cut short by the
          // window.location.href navigation just below when a purchase was
          // pending — that's the one cohort where losing this record would
          // matter most.
          await acceptTerms(data.session.access_token);
          const pendingSku = sessionStorage.getItem('pendingSku');
          if (pendingSku) {
            sessionStorage.removeItem('pendingSku');
            const { url } = await createCheckoutSession(pendingSku, data.session.access_token);
            window.location.href = url;
            return;
          }
        } else {
          setSignUpSuccess(true);
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        dispatch(setSession(data.session));
        dispatch(setUser(data.session.user));
        const pendingSku = sessionStorage.getItem('pendingSku');
        if (pendingSku) {
          sessionStorage.removeItem('pendingSku');
          const { url } = await createCheckoutSession(pendingSku, data.session.access_token);
          window.location.href = url;
          return;
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}>
      <div style={styles.modal}>
        {onClose && (
          <button onClick={onClose} style={styles.closeBtn} aria-label="Close">✕</button>
        )}
        {/* Logo */}
        <div style={styles.logoRow}>
          <span style={styles.logo}>ZYNACULAR</span>
        </div>
        <p style={styles.tagline}>From raw resume to interview-ready.</p>

        {signUpSuccess ? (
          <div style={styles.successBox}>
            <p style={styles.forgotHeading}>Check your email</p>
            <p style={styles.successText}>
              We sent a confirmation link to {email}. Click it to verify your address, then sign in.
            </p>
            <p style={styles.successNote}>
              Not there? Check your spam or junk folder — new senders sometimes land there. The link is good for one hour.
            </p>
            <button
              style={styles.switchBtn}
              onClick={() => { setSignUpSuccess(false); setMode('login'); }}
            >
              Back to sign in
            </button>
          </div>
        ) : mode === 'forgot' ? (
          /* ── Forgot password view ── */
          resetSent ? (
            <div style={styles.successBox}>
              <p style={styles.successText}>Check your email for a reset link.</p>
              <p style={styles.successNote}>
                Not there? Check your spam or junk folder — new senders sometimes land there. The link is good for one hour.
              </p>
              <button
                style={styles.switchBtn}
                onClick={() => { setResetSent(false); setMode('login'); setError(null); }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <p style={styles.forgotHeading}>Reset your password</p>
              <p style={styles.forgotSub}>Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleForgot} style={styles.form}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    style={styles.input}
                  />
                </div>
                {error && <p style={styles.error}>{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  style={{ ...styles.submitBtn, ...(loading ? styles.submitBtnDisabled : {}) }}
                >
                  {loading ? 'Please wait…' : 'Send reset link'}
                </button>
              </form>
              <button
                style={styles.mutedLink}
                onClick={() => { setMode('login'); setError(null); }}
              >
                Back to sign in
              </button>
            </>
          )
        ) : (
          <>
            {/* Mode tabs */}
            <div style={styles.tabs}>
              <button
                style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
                onClick={() => { setMode('login'); setError(null); setPasswordFocused(false); setTermsAccepted(false); }}
              >
                Sign In
              </button>
              <button
                style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }}
                onClick={() => { setMode('signup'); setError(null); setPasswordFocused(false); setTermsAccepted(false); }}
              >
                Sign Up
              </button>
            </div>

            {mode === 'signup' && (
              <p style={styles.freeNote}>Free to create an account.</p>
            )}

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  required
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  placeholder="••••••••"
                  style={styles.input}
                />
                {mode === 'login' && (
                  <button
                    type="button"
                    style={styles.mutedLink}
                    onClick={() => { setMode('forgot'); setError(null); }}
                  >
                    Forgot password?
                  </button>
                )}
                {mode === 'signup' && passwordFocused && (
                  <PasswordRequirements password={password} />
                )}
              </div>

              {mode === 'signup' && (
                <label htmlFor="signup-terms-checkbox" style={styles.checkboxRow}>
                  <input
                    id="signup-terms-checkbox"
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    style={styles.checkboxInput}
                  />
                  <span style={styles.checkboxLabel}>
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.consentLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.consentLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </Link>
                  </span>
                </label>
              )}

              {error && <p style={styles.error}>{error}</p>}

              <button
                type="submit"
                disabled={loading || (mode === 'signup' && (!isPasswordValid(password) || !termsAccepted))}
                style={{ ...styles.submitBtn, ...((loading || (mode === 'signup' && (!isPasswordValid(password) || !termsAccepted))) ? styles.submitBtnDisabled : {}) }}
              >
                {loading
                  ? 'Please wait…'
                  : mode === 'signup'
                  ? 'Create Account'
                  : 'Sign In'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  closeBtn: {
    position: 'absolute',
    top: '14px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    color: '#6B7FA8',
    fontSize: '16px',
    cursor: 'pointer',
    lineHeight: 1,
    padding: '4px',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(10, 15, 30, 0.92)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    position: 'relative',
    background: 'linear-gradient(145deg, rgba(15,22,41,0.95) 0%, rgba(10,15,30,0.98) 100%)',
    border: '1px solid #1E2D4A',
    borderRadius: '16px',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  logoRow: {
    textAlign: 'center',
    marginBottom: '6px',
  },
  logo: {
    fontFamily: "'Syne', sans-serif",
    fontSize: '24px',
    fontWeight: 800,
    letterSpacing: '0.15em',
    background: 'linear-gradient(135deg, #2E6AFF 0%, #00D4A0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  tagline: {
    textAlign: 'center',
    fontSize: '11px',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#6B7FA8',
    marginBottom: '32px',
  },
  tabs: {
    display: 'flex',
    borderRadius: '8px',
    background: '#0A0F1E',
    border: '1px solid #1E2D4A',
    padding: '4px',
    marginBottom: '24px',
    gap: '4px',
  },
  tab: {
    flex: 1,
    padding: '8px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: '#6B7FA8',
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    letterSpacing: '0.05em',
  },
  tabActive: {
    background: '#1E2D4A',
    color: '#E8EEFF',
  },
  freeNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#6B7FA8',
    marginBottom: '18px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6B7FA8',
  },
  input: {
    background: '#0A0F1E',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#E8EEFF',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  error: {
    fontSize: '13px',
    color: '#ff6b6b',
    margin: 0,
    padding: '10px 12px',
    background: 'rgba(255, 107, 107, 0.08)',
    borderRadius: '6px',
    border: '1px solid rgba(255, 107, 107, 0.2)',
  },
  submitBtn: {
    marginTop: '4px',
    padding: '12px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #2E6AFF 0%, #1a4fd6 100%)',
    color: '#fff',
    fontFamily: "'Outfit', system-ui, sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease, transform 0.15s ease',
    boxShadow: '0 4px 20px rgba(46, 106, 255, 0.35)',
  },
  submitBtnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  successBox: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  successText: {
    color: '#00D4A0',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  successNote: {
    color: '#6B7FA8',
    fontSize: '12px',
    lineHeight: 1.55,
  },
  switchBtn: {
    background: 'transparent',
    border: '1px solid #1E2D4A',
    borderRadius: '8px',
    color: '#6B7FA8',
    padding: '8px 20px',
    cursor: 'pointer',
    fontSize: '13px',
    fontFamily: 'inherit',
  },
  mutedLink: {
    background: 'transparent',
    border: 'none',
    color: '#4E6385',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: "'Outfit', system-ui, sans-serif",
    padding: '2px 0',
    textAlign: 'left',
    letterSpacing: '0.01em',
  },
  forgotHeading: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#E8EEFF',
    marginBottom: '6px',
    fontFamily: "'Syne', sans-serif",
    letterSpacing: '0.02em',
  },
  forgotSub: {
    fontSize: '13px',
    color: '#6B7FA8',
    marginBottom: '24px',
    lineHeight: 1.55,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    cursor: 'pointer',
    marginTop: '-4px',
  },
  checkboxInput: {
    marginTop: '2px',
    width: '14px',
    height: '14px',
    flexShrink: 0,
    accentColor: '#2E6AFF',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '12px',
    color: '#8A9DC0',
    lineHeight: 1.5,
  },
  consentLink: {
    color: '#8BA3CC',
    textDecoration: 'underline',
  },
};
